/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import Img from "../components/Img";

const style = {
  wrap: {background:"var(--cream, #FAF8F3)", minHeight:"100vh", fontFamily:"'Jost', sans-serif", color:"var(--navy, #1B2340)"},
  main: {maxWidth:1200, margin:"0 auto", padding:"32px 24px 60px"},
  eyebrow: {fontSize:11, letterSpacing:"0.18em", color:"var(--gold, #B8913A)", textTransform:"uppercase"},
  h1: {fontFamily:"'Cormorant Garamond', serif", fontSize:"clamp(38px, 6vw, 60px)", lineHeight:1.02, margin:"6px 0 8px"},
  h2: {fontFamily:"'Cormorant Garamond', serif", fontSize:32, margin:"36px 0 16px"},
  meta: {color:"#666", fontSize:13, letterSpacing:"0.04em"},
  banner: {background:"var(--navy, #1B2340)", color:"#fff", fontSize:11, letterSpacing:"0.14em", textAlign:"center", padding:"8px", textTransform:"uppercase"},
  gallery: {display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:8, marginTop:20, gridAutoRows:"180px"},
  galleryImg: {width:"100%", height:"100%", objectFit:"cover", borderRadius:6},
  card: {background:"#fff", border:"1px solid #eee", borderRadius:8, overflow:"hidden", display:"flex", flexDirection:"column"},
  cardBody: {padding:"14px 16px 16px", display:"flex", flexDirection:"column", gap:4, flex:1},
  cardImage: {width:"100%", aspectRatio:"4/3", objectFit:"cover", background:"#eee"},
  grid: {display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:20},
  amenityRow: {display:"flex", flexWrap:"wrap", gap:6, marginTop:12},
  chip: {fontSize:11, padding:"4px 10px", background:"rgba(184,145,58,0.12)", color:"var(--navy, #1B2340)", borderRadius:999, letterSpacing:"0.04em"},
};

function priceLabel(cents, unit) {
  if (!cents) return null;
  return `$${(cents/100).toFixed(0)}/${unit || "hr"}`;
}

function ResourceCard({ loc, r, kind }) {
  const to = `/locations/${loc.slug}/${kind}s/${r.slug}`;
  return (
    <Link to={to} style={{...style.card, textDecoration:"none", color:"inherit"}} data-testid={`v2-card-${r.slug}`}>
      {r.primary_image
        ? <img src={r.primary_image} alt={`${r.name}, ${loc.city}`} style={style.cardImage} loading="lazy" />
        : <div style={style.cardImage} />}
      <div style={style.cardBody}>
        <div style={{fontSize:11, letterSpacing:"0.12em", color:"var(--gold)", textTransform:"uppercase"}}>
          {r.workspace_type || r.venue_type}
        </div>
        <div style={{fontFamily:"'Cormorant Garamond', serif", fontSize:22}}>{r.name}</div>
        {r.capacity ? <div style={style.meta}>Up to {r.capacity}</div> : null}
        <div style={{marginTop:"auto", display:"flex", justifyContent:"space-between", alignItems:"end", paddingTop:10}}>
          <div style={{fontSize:13}}>{priceLabel(r.starting_price_cents, r.starting_price_unit) || "See details"}</div>
          <div style={{fontSize:11, letterSpacing:"0.08em"}}>{r.booking_mode?.replace(/_/g," ")}</div>
        </div>
      </div>
    </Link>
  );
}

export default function LocationDetail() {
  const { locationSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/inventory-v2/locations/${locationSlug}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, [locationSlug]);

  if (error) return <div style={{...style.wrap, padding:60}}>Error: {error}</div>;
  if (!data) return <div style={{...style.wrap, padding:60}}>Loading…</div>;

  const { location: loc, organization: org, workspaces, venues } = data;
  const gallery = loc.images || [];
  return (
    <div style={style.wrap} data-testid="v2-location-detail">
      <div style={style.banner}>Métier · Inventory V2 Preview · Admin only</div>
      <main style={style.main}>
        <span style={style.eyebrow}>{org?.name || "Métier location"}</span>
        <h1 style={style.h1}>{loc.name}</h1>
        <div style={style.meta}>{loc.address}{loc.city ? ` · ${loc.city}` : ""}{loc.state ? `, ${loc.state}` : ""}</div>
        {loc.description ? <p style={{maxWidth:720, marginTop:14, lineHeight:1.7}}>{loc.description}</p> : null}
        {gallery.length ? (
          <div style={style.gallery}>
            {gallery.slice(0,3).map((g,i) => <Img key={i} src={g} alt={`${loc.name} ${i+1}`} style={style.galleryImg} />)}
          </div>
        ) : null}
        {loc.amenities_location?.length ? (
          <div style={style.amenityRow}>
            {loc.amenities_location.map(a => <span key={a} style={style.chip}>{a}</span>)}
          </div>
        ) : null}

        {workspaces.length > 0 && (
          <>
            <h2 style={style.h2}>Available Workspaces <span style={{fontSize:14, color:"#888"}}>({workspaces.length})</span></h2>
            <div style={style.grid} data-testid="v2-workspaces-grid">
              {workspaces.map(r => <ResourceCard key={r.slug} loc={loc} r={r} kind="workspace" />)}
            </div>
          </>
        )}
        {venues.length > 0 && (
          <>
            <h2 style={style.h2}>Available Venues <span style={{fontSize:14, color:"#888"}}>({venues.length})</span></h2>
            <div style={style.grid} data-testid="v2-venues-grid">
              {venues.map(r => <ResourceCard key={r.slug} loc={loc} r={r} kind="venue" />)}
            </div>
          </>
        )}
        {workspaces.length === 0 && venues.length === 0 && (
          <p style={{...style.meta, marginTop:32}}>No published inventory yet at this location.</p>
        )}
      </main>
    </div>
  );
}

// Shared style export for the sibling detail pages.
export { style };
